import React, { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Loader, Trash2, RotateCcw, AlertTriangle, Edit, X, CreditCard, Eye, EyeOff, Upload, ExternalLink, Calendar, DollarSign, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { servicesService } from './servicesService';
import { AddServiceModal } from './AddServiceModal';
import { EditServiceModal } from './EditServiceModal';
import { ImportServicesModal } from './ImportServicesModal';
import { findDuplicates, getDuplicateCount } from './duplicateDetection';
import { Service, Office } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { getExchangeRates, convertToNOK, formatCurrency } from '../../lib/exchangeRateService';

const OFFICE_COLORS: Record<Office, { bg: string; text: string; border: string }> = {
  'stavanger': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  'tønsberg': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
  'oslo': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'shared': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
};

const OFFICE_LABELS: Record<Office, string> = {
  'stavanger': 'Stavanger',
  'tønsberg': 'Tønsberg',
  'oslo': 'Oslo',
  'shared': 'Shared',
};

export const ServicesModule: React.FC = () => {
  const { user, permissions, canDeleteContent } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'deleted'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [officeFilter, setOfficeFilter] = useState<Office | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [showAllTags, setShowAllTags] = useState(false);
  const [tabCounts, setTabCounts] = useState({ active: 0, deleted: 0 });
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({ NOK: 1 });

  useEffect(() => {
    loadServices();
    getExchangeRates().then(setExchangeRates);
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const fetched = await servicesService.getServices();
      fetched.sort((a, b) => a.name.localeCompare(b.name, 'nb', { sensitivity: 'base' }));
      setServices(fetched);

      const counts = {
        active: fetched.filter(s => !s.deleted).length,
        deleted: fetched.filter(s => s.deleted).length,
      };
      setTabCounts(counts);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Duplicate detection
  const duplicateMap = useMemo(() => findDuplicates(services), [services]);

  // Cost summary (converted to NOK)
  const costSummary = useMemo(() => {
    const activeServices = services.filter(s => !s.deleted);
    let totalMonthly = 0;
    let totalYearly = 0;

    activeServices.forEach(s => {
      if (s.cost && s.cost > 0) {
        const costInNOK = convertToNOK(s.cost, s.currency || 'NOK', exchangeRates);
        if (s.billingCycle === 'monthly') {
          totalMonthly += costInNOK;
          totalYearly += costInNOK * 12;
        } else if (s.billingCycle === 'yearly') {
          totalMonthly += costInNOK / 12;
          totalYearly += costInNOK;
        } else if (s.billingCycle === 'one-time') {
          // Don't include in recurring costs
        }
      }
    });

    return { totalMonthly: Math.round(totalMonthly), totalYearly: Math.round(totalYearly) };
  }, [services, exchangeRates]);

  // Categories
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    services.filter(s => !s.deleted).forEach(s => {
      if (s.category?.trim()) cats.add(s.category.trim());
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b, 'nb'));
  }, [services]);

  // Tags
  const getAllTagsWithCounts = () => {
    const tagCounts = new Map<string, number>();
    const relevant = services.filter(s => {
      if (activeTab === 'active') return !s.deleted;
      if (activeTab === 'deleted') return s.deleted;
      return true;
    });

    relevant.forEach(service => {
      service.tags.forEach(tag => {
        if (tag.trim()) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      });
    });

    return Array.from(tagCounts.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);
  };

  const getPopularTags = () => getAllTagsWithCounts().slice(0, 10);

  const handlePopularTagClick = (tag: string) => {
    if (!activeFilters.includes(tag)) {
      setActiveFilters(prev => [...prev, tag]);
    }
  };

  const removeFilter = (filterToRemove: string) => {
    setActiveFilters(prev => prev.filter(f => f !== filterToRemove));
  };

  const clearAllFilters = () => {
    setActiveFilters([]);
    setSearchTerm('');
    setOfficeFilter('all');
    setCategoryFilter('all');
  };

  const getAllExistingTags = () => {
    return Array.from(new Set(services.flatMap(s => s.tags))).sort();
  };

  const toggleExpanded = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Actions
  const handleEdit = (service: Service) => {
    setEditingService(service);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!user) return;
    if (window.confirm('Are you sure you want to delete this service? It will be moved to the Deleted tab.')) {
      try {
        await servicesService.deleteService(id, user.email!);
        await loadServices();
      } catch (error) {
        console.error('Failed to delete service:', error);
      }
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await servicesService.restoreService(id);
      await loadServices();
    } catch (error) {
      console.error('Failed to restore service:', error);
    }
  };

  const handlePermanentDelete = async (id: string) => {
    if (!user) return;
    if (window.confirm('Are you sure you want to permanently delete this service? This cannot be undone.')) {
      try {
        await servicesService.permanentlyDeleteService(id);
        await loadServices();
      } catch (error) {
        console.error('Failed to permanently delete service:', error);
      }
    }
  };

  const togglePassword = (id: string) => {
    setVisiblePasswords(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // Permission helpers
  const canUserDelete = (service: Service) => canDeleteContent(service.createdBy);
  const canUserEdit = (service: Service) => user?.email === service.createdBy || permissions.canManageContent;
  const canUserRestore = () => permissions.canUploadContent;
  const canUserPermanentDelete = () => permissions.canManageContent;

  // Filtering
  const filteredServices = services.filter(service => {
    // Tab filter
    let matchesTab = false;
    if (activeTab === 'active') matchesTab = !service.deleted;
    else if (activeTab === 'deleted') matchesTab = service.deleted;
    if (!matchesTab) return false;

    // Office filter
    if (officeFilter !== 'all' && service.office !== officeFilter) return false;

    // Category filter
    if (categoryFilter !== 'all' && service.category !== categoryFilter) return false;

    // Tag filters (AND logic)
    const passesFilters = activeFilters.every(filter => {
      const fl = filter.toLowerCase();
      const basicMatch = [service.name, service.description, service.category, service.notes, service.url, service.createdBy]
        .some(field => (field || '').toLowerCase().includes(fl));
      const tagMatch = service.tags.some(tag => tag.toLowerCase().includes(fl));
      return basicMatch || tagMatch;
    });
    if (!passesFilters) return false;

    // Search
    if (!searchTerm) return true;
    const sl = searchTerm.toLowerCase();
    const basicMatch = [service.name, service.description, service.category, service.notes, service.url, service.username, service.createdBy]
      .some(field => (field || '').toLowerCase().includes(sl));
    const tagMatch = service.tags.some(tag => tag.toLowerCase().includes(sl));
    return basicMatch || tagMatch;
  });

  // Format cost in original currency
  const formatCostOriginal = (service: Service) => {
    if (!service.cost || service.billingCycle === 'free') return 'Free';
    const currency = service.currency || 'NOK';
    const base = formatCurrency(service.cost, currency);
    if (service.billingCycle === 'monthly') return `${base}/mo`;
    if (service.billingCycle === 'yearly') return `${base}/yr`;
    if (service.billingCycle === 'one-time') return `${base} (one-time)`;
    return base;
  };

  // Renewal — stored date is just day (+ month for yearly), year is irrelevant.
  // We compute the next occurrence to check proximity.
  const getRenewalStatus = (date: Date | null, billingCycle?: string) => {
    if (!date) return null;
    const day = date.getDate();
    const month = date.getMonth(); // 0-indexed

    if (billingCycle === 'monthly') {
      // Next occurrence is this month's day, or next month if already passed
      const now = new Date();
      let next = new Date(now.getFullYear(), now.getMonth(), day);
      if (next < now) next = new Date(now.getFullYear(), now.getMonth() + 1, day);
      const days = Math.ceil((next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (days <= 7) return { label: `${days}d`, color: 'text-amber-600 bg-amber-50' };
      return { label: `${day}.`, color: 'text-gray-600 bg-gray-50' };
    }

    // Yearly (or other): next occurrence is this year's date, or next year if passed
    const now = new Date();
    let next = new Date(now.getFullYear(), month, day);
    if (next < now) next = new Date(now.getFullYear() + 1, month, day);
    const days = Math.ceil((next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 30) return { label: `${days}d`, color: 'text-amber-600 bg-amber-50' };
    return { label: `${day}. ${date.toLocaleDateString('nb-NO', { month: 'short' })}`, color: 'text-gray-600 bg-gray-50' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading services...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <CreditCard size={32} className="text-primary" />
          <div>
            <h2 className="text-2xl font-bold">Services & Subscriptions</h2>
            <p className="text-muted-foreground">{services.filter(s => !s.deleted).length} active services</p>
          </div>
        </div>

        <div className="flex gap-2">
          {permissions.canUploadContent && (
            <>
              <button
                onClick={() => setShowImportModal(true)}
                className="border border-gray-300 text-foreground px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-accent"
              >
                <Upload size={20} />
                Import CSV
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Add Service
              </button>
            </>
          )}
        </div>
      </div>

      {/* Cost Summary */}
      {!searchTerm && activeFilters.length === 0 && officeFilter === 'all' && categoryFilter === 'all' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border rounded-lg p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Est. Monthly Cost</p>
              <p className="text-xl font-bold">kr {new Intl.NumberFormat('nb-NO').format(costSummary.totalMonthly)}</p>
            </div>
          </div>
          <div className="bg-white border rounded-lg p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <DollarSign size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Est. Yearly Cost</p>
              <p className="text-xl font-bold">kr {new Intl.NumberFormat('nb-NO').format(costSummary.totalYearly)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters row */}
      <div className="flex gap-4 flex-wrap items-center">
        {/* Office Filter */}
        <div className="flex gap-2">
          {(['all', 'stavanger', 'tønsberg', 'oslo', 'shared'] as const).map(office => (
            <button
              key={office}
              onClick={() => setOfficeFilter(office)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                officeFilter === office
                  ? office === 'all'
                    ? 'bg-gray-900 text-white'
                    : `${OFFICE_COLORS[office as Office].bg} ${OFFICE_COLORS[office as Office].text} border ${OFFICE_COLORS[office as Office].border}`
                  : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {office === 'all' ? 'All Offices' : OFFICE_LABELS[office as Office]}
            </button>
          ))}
        </div>

        {/* Category Filter */}
        {allCategories.length > 0 && (
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 cursor-pointer"
          >
            <option value="all">All Categories</option>
            {allCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        )}
      </div>

      {/* Search */}
      <div className="space-y-4">
        <div className="relative max-w-2xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            placeholder="Search services by name, category, URL, username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 py-3 border rounded-lg w-full text-base"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground font-medium">Active filters:</span>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map(filter => (
                  <span
                    key={filter}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200"
                  >
                    {filter}
                    <button onClick={() => removeFilter(filter)} className="hover:text-blue-600">
                      <X size={14} />
                    </button>
                  </span>
                ))}
                <button onClick={clearAllFilters} className="text-xs text-muted-foreground hover:text-foreground underline">
                  Clear all
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popular Tags */}
        {!searchTerm && activeFilters.length === 0 && services.length > 0 && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Popular tags:</p>
            <div className="flex flex-wrap gap-2">
              {(showAllTags ? getAllTagsWithCounts() : getPopularTags()).map(({ tag, count }) => (
                <button
                  key={tag}
                  onClick={() => handlePopularTagClick(tag)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors flex items-center gap-1"
                >
                  {tag}
                  <span className="text-xs text-blue-500">({count})</span>
                </button>
              ))}
              {!showAllTags && getAllTagsWithCounts().length > 10 && (
                <button
                  onClick={() => setShowAllTags(true)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  See all ({getAllTagsWithCounts().length})
                </button>
              )}
              {showAllTags && (
                <button
                  onClick={() => setShowAllTags(false)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}

        {/* Search Results Summary */}
        {(searchTerm || activeFilters.length > 0 || officeFilter !== 'all' || categoryFilter !== 'all') && (
          <div className="text-sm text-muted-foreground">
            Found {filteredServices.length} {filteredServices.length === 1 ? 'service' : 'services'}
            {activeFilters.length > 0 && ` matching ${activeFilters.length} filter${activeFilters.length === 1 ? '' : 's'}`}
            {searchTerm && ` and search term "${searchTerm}"`}
            {officeFilter !== 'all' && ` in ${OFFICE_LABELS[officeFilter]}`}
            {categoryFilter !== 'all' && ` in category "${categoryFilter}"`}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {(['active', 'deleted'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)} ({tabCounts[tab]})
            </button>
          ))}
        </nav>
      </div>

      {/* Empty state */}
      {filteredServices.length === 0 && !loading && (
        <div className="text-center py-12">
          {services.length === 0 ? (
            <p className="text-muted-foreground">
              No services added yet. Click &apos;Add Service&apos; or &apos;Import CSV&apos; to get started!
            </p>
          ) : (
            <div>
              <p className="text-muted-foreground">No services found matching your filters.</p>
              {(activeFilters.length > 0 || searchTerm || officeFilter !== 'all') && (
                <button onClick={clearAllFilters} className="text-primary hover:underline mt-2">
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Services List */}
      {filteredServices.length > 0 && (
        <div className="bg-white border rounded-lg divide-y">
          {filteredServices.map(service => {
            const dupCount = getDuplicateCount(service.id, duplicateMap);
            const renewalStatus = getRenewalStatus(service.renewalDate, service.billingCycle);
            const isExpanded = expandedRows.has(service.id);

            return (
              <div key={service.id}>
                {/* Collapsed row */}
                <button
                  onClick={() => toggleExpanded(service.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                >
                  {isExpanded ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}

                  {/* Name */}
                  <span className="font-medium text-sm min-w-0 truncate flex-shrink-0 w-48">{service.name}</span>

                  {/* Office badge */}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${OFFICE_COLORS[service.office].bg} ${OFFICE_COLORS[service.office].text} border ${OFFICE_COLORS[service.office].border}`}>
                    {OFFICE_LABELS[service.office]}
                  </span>

                  {/* Duplicate badge */}
                  {dupCount > 0 && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full border border-orange-200 flex-shrink-0 flex items-center gap-1">
                      <Copy size={10} />
                      {dupCount}
                    </span>
                  )}

                  {/* Category */}
                  {service.category && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full flex-shrink-0 hidden sm:inline">
                      {service.category}
                    </span>
                  )}

                  {/* Description (truncated) */}
                  <span className="text-xs text-muted-foreground truncate min-w-0 flex-1 hidden md:inline">
                    {service.description}
                  </span>

                  {/* Renewal */}
                  {renewalStatus && (
                    <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 hidden lg:inline-flex items-center gap-1 ${renewalStatus.color}`}>
                      <Calendar size={10} />
                      {renewalStatus.label}
                    </span>
                  )}

                  {/* Cost */}
                  <span className="text-sm font-medium text-right flex-shrink-0 w-28 hidden sm:inline">
                    {formatCostOriginal(service)}
                  </span>

                  {/* Deleted indicator */}
                  {service.deleted && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full flex-shrink-0">
                      Deleted
                    </span>
                  )}
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 ml-7 space-y-3 border-t border-dashed">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Left column: details */}
                      <div className="space-y-3">
                        {/* URL */}
                        {service.url && (
                          <div>
                            <span className="text-xs text-muted-foreground block mb-0.5">URL</span>
                            <a
                              href={service.url.startsWith('http') ? service.url : `https://${service.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink size={12} />
                              {service.url.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                        )}

                        {/* Credentials */}
                        {(service.username || service.password) && (
                          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            {service.username && (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Username</span>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono text-sm">{service.username}</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(service.username); }}
                                    className="text-gray-400 hover:text-gray-600 p-0.5"
                                    title="Copy"
                                  >
                                    <Copy size={12} />
                                  </button>
                                </div>
                              </div>
                            )}
                            {service.password && (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Password</span>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono text-sm">
                                    {visiblePasswords.has(service.id) ? service.password : '••••••••'}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); togglePassword(service.id); }}
                                    className="text-gray-400 hover:text-gray-600 p-0.5"
                                    title={visiblePasswords.has(service.id) ? 'Hide' : 'Show'}
                                  >
                                    {visiblePasswords.has(service.id) ? <EyeOff size={12} /> : <Eye size={12} />}
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(service.password); }}
                                    className="text-gray-400 hover:text-gray-600 p-0.5"
                                    title="Copy"
                                  >
                                    <Copy size={12} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Description & Notes */}
                        {service.description && (
                          <div>
                            <span className="text-xs text-muted-foreground block mb-0.5">Description</span>
                            <p className="text-sm">{service.description}</p>
                          </div>
                        )}
                        {service.notes && (
                          <div>
                            <span className="text-xs text-muted-foreground block mb-0.5">Notes</span>
                            <p className="text-sm italic text-muted-foreground">{service.notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Right column: meta + cost */}
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-xs text-muted-foreground block mb-0.5">Cost</span>
                            <span className="font-medium">{formatCostOriginal(service)}</span>
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground block mb-0.5">Billing</span>
                            <span className="capitalize">{service.billingCycle}</span>
                          </div>
                          {renewalStatus && (
                            <div>
                              <span className="text-xs text-muted-foreground block mb-0.5">Renewal</span>
                              <span className={`inline-flex items-center gap-1 text-sm ${renewalStatus.color.split(' ')[0]}`}>
                                <Calendar size={12} />
                                {renewalStatus.label}
                              </span>
                            </div>
                          )}
                          <div>
                            <span className="text-xs text-muted-foreground block mb-0.5">Added by</span>
                            <span>{service.createdBy}</span>
                          </div>
                        </div>

                        {/* Tags */}
                        {service.tags.length > 0 && (
                          <div>
                            <span className="text-xs text-muted-foreground block mb-1">Tags</span>
                            <div className="flex flex-wrap gap-1">
                              {service.tags.map(tag => (
                                <span key={tag} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          {!service.deleted ? (
                            <>
                              {canUserEdit(service) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEdit(service); }}
                                  className="px-3 py-1.5 text-foreground rounded-lg flex items-center gap-1.5 hover:bg-purple-100 hover:text-purple-600 transition-colors border border-gray-200 hover:border-purple-200 text-xs"
                                >
                                  <Edit size={12} />
                                  Edit
                                </button>
                              )}
                              {canUserDelete(service) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(service.id); }}
                                  className="px-3 py-1.5 text-foreground rounded-lg flex items-center gap-1.5 hover:bg-red-100 hover:text-red-600 transition-colors border border-gray-200 hover:border-red-200 text-xs"
                                >
                                  <Trash2 size={12} />
                                  Delete
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              {canUserRestore() && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRestore(service.id); }}
                                  className="px-3 py-1.5 text-foreground rounded-lg flex items-center gap-1.5 hover:bg-green-100 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200 text-xs"
                                >
                                  <RotateCcw size={12} />
                                  Restore
                                </button>
                              )}
                              {canUserPermanentDelete() && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handlePermanentDelete(service.id); }}
                                  className="px-3 py-1.5 text-foreground rounded-lg flex items-center gap-1.5 hover:bg-red-100 hover:text-red-700 transition-colors border border-gray-200 hover:border-red-200 text-xs"
                                >
                                  <AlertTriangle size={12} />
                                  Permanently Delete
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <AddServiceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadServices}
        existingTags={getAllExistingTags()}
      />

      <EditServiceModal
        isOpen={showEditModal}
        service={editingService}
        existingTags={getAllExistingTags()}
        onClose={() => {
          setShowEditModal(false);
          setEditingService(null);
        }}
        onSuccess={loadServices}
      />

      <ImportServicesModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={loadServices}
      />
    </div>
  );
};
